import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Breadcrumbs from '../common/Breadcrumbs';
import Icon from '../common/Icon';
import { userCan } from '../../helpers/rolePermissions';
import SubmissionDeliveries from '../deliveries/SubmissionDeliveries';
import FieldResponseDisplay from './sub-show-components/FieldResponseDisplay';
import FieldsetDisplay from './sub-show-components/FieldsetDisplay';
import { createResponse } from './constants/handleResponseData';
import {
  partitionFields,
  relatedResponse,
  stickyHeader,
  markSubmissionRead,
  fetchDelivery,
  fieldsetHasValidResponses,
} from './constants/submissionShow';
import { LoadSpinner } from '../common/LoadSpinner';
import SingleMailerModal from '../mailers/SingleMailerModal';
import QuotaNotifyStatus from './quotas/QuotaNotifyStatus';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import LinkedSubmissionsModal from './sub-show-components/linked-submissions/LinkedSubmissionsModal';
import { SubmissionContext } from '../../hooks/Contexts';
import { notify } from '../../helpers/notifications';
import { sortable } from '../../helpers/sorters';

// Note: destructuring useLoaderData was failing to render properly when hopping between submissions
// The solution was to explicitly set state for each data piece
// This can be optimized and cleaned up later... test carefully
const SubmissionShow = () => {
  const { data } = useLoaderData();
  const nav = useNavigate();
  const [submission, setSubmission] = useState(data.submission);
  const [fieldsets, setFieldsets] = useState(data.fieldsets);
  const [fields, setFields] = useState(data.fields);
  const [responses, setResponses] = useState(data.submission.responses);
  const [deliveries, setDeliveries] = useState(data.submission.deliveries);
  const [groupState, setGroupState] = useState(data.group);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMailSend, setViewMailSend] = useState(false);
  const [showQuotaStatusModal, setShowQuotaStatusModal] = useState(false);
  const [showLinkedModal, setShowLinkedModal] = useState(false);
  const [quotaOpt, setQuotaOpt] = useState(null);
  const [prevQuotaOpt, setPrevQuotaOpt] = useState(null);
  const isParent = groupState
    ? groupState.parent_submission_id === data.submission.id
    : false;
  const canBecomeParent = data.form.is_parent && !isParent;
  const revalidator = useRevalidator();
  const reloadQuotaFieldData = () => revalidator.revalidate();
  const handleBack = (e) => {
    e.preventDefault();
    if (window.history.length > 1) {
      window.history.back();
    } else {
      nav(`/manage/forms/${data.form.id}`);
    }
  };

  useEffect(() => {
    if (!data.submission.read) markSubmissionRead(data.submission.id);
    let promises = [];
    let presentResponses = data.submission.responses.map((r) => r.field_id);
    let missingResponses = data.fields
      .map((f) => f.id)
      .filter((id) => !presentResponses.includes(id));

    if (missingResponses.length) {
      missingResponses.forEach((fieldId) => {
        promises.push(createResponse(data.submission.id, fieldId));
      });
      Promise.all(promises).then((newResponses) => {
        let errors = [];
        newResponses = newResponses.reduce((arr, res) => {
          if (res.data.errors) {
            errors.push(res);
          } else {
            arr.push(res.data);
          }
          return arr;
        }, []);

        // do something with errors here
        if (errors.length > 0) {
          console.error(errors);
        }

        setResponses([...data.submission.responses, ...newResponses]);
        setIsLoading(false);
      });
    } else {
      setResponses(data.submission.responses);
      setIsLoading(false);
    }
    setSubmission(data.submission);
    setDeliveries(data.submission.deliveries);
    setFieldsets(data.fieldsets);
    setFields(data.fields);
    setGroupState(data.group);
    setIsEditing(false);
  }, [data.submission.id]);

  useEffect(() => {
    document.addEventListener('scroll', stickyHeader());
    return () => {
      document.removeEventListener('scroll', stickyHeader());
      setIsLoading(true);
    };
  }, [location.pathname]);

  const updateDeliveries = async (delivery) => {
    setDeliveries([...deliveries, delivery]);
    var wait = setTimeout(async () => {
      let reload = await fetchDelivery(delivery.id);
      if (reload.ok) {
        setDeliveries((deliveries) => {
          let others = deliveries.filter((d) => d.id != delivery.id);
          return [...others, reload.data];
        });
        if (reload.data.mailed_at) {
          notify.success('Mail sent successfully');
        }
      }
      clearTimeout(wait);
    }, 3000);
  };

  const user = { ...userCan(data.current_user, data.permission) };
  const selectableMailers = user.canWrite
    ? data.mailers
    : data.mailers.filter((m) => m.universal_mail_send);
  const publicFieldsets = sortable(fieldsets.filter((fs) => fs.public));
  const privateFieldsets = sortable(fieldsets.filter((fs) => !fs.public));
  const closeQuotaModal = () => {
    setQuotaOpt(null);
    setPrevQuotaOpt(null);
    setShowQuotaStatusModal(false);
  };

  return (
    <SubmissionContext.Provider
      value={{ submission, groupState, isEditing }}
      key={`sub-${submission.id}`}
    >
      <Breadcrumbs form={data.form} container={data.container} />
      {isLoading ? (
        <LoadSpinner />
      ) : (
        <div className='submission' key={`sub-id-${submission.id}`}>
          <div className='form actions head'>
            <div className='left controls'>
              <a className='icon-only' onClick={handleBack}>
                <i className='fa-solid fa-circle-left' aria-hidden='true'></i>
              </a>
            </div>

            <div className='left sub-id-info'>
              <div>
                <h4>
                  ID: <span className='supplemental'>{submission.id}</span>
                  {isParent && (
                    <span title='Parent Submission'>
                      &nbsp;
                      <i className='fa-solid fa-star' />
                    </span>
                  )}
                </h4>
                {groupState?.id && (
                  <span className='controls'>
                    &nbsp;
                    <Icon
                      className='icon-only link-icon'
                      icon='link'
                      title='Linked Submissions'
                      onClick={() => setShowLinkedModal(true)}
                    />
                  </span>
                )}
              </div>

              {groupState?.id && !isParent && (
                <h4>
                  Parent ID:{' '}
                  <span className='supplemental'>
                    {groupState.parent_submission_id}
                  </span>
                </h4>
              )}
              {data.form.course_validate && (
                <h4>
                  &nbsp;Completion Code:{' '}
                  <span className='supplemental'>{submission.course_code}</span>
                </h4>
              )}
            </div>

            <div className='right'>
              <h4>
                Submitted:{' '}
                <time dateTime={submission.created_at}>
                  {dayjs(submission.created_at).format('MM/DD/YYYY h:mm A')}
                </time>
              </h4>
              <div className='controls top-mailer'>
                {data.form.is_parent && (
                  <Icon
                    title='Manage Linked Submissions'
                    icon='link'
                    onClick={() => setShowLinkedModal(true)}
                  />
                )}
                {user.canWrite && (
                  <Icon
                    className='edit'
                    icon='save'
                    title='Save Form'
                    onClick={() => setIsEditing(false)}
                  />
                )}
                {(user.canWrite ||
                  data.mailers.filter((m) => m.universal_mail_send)) && (
                  <Icon
                    title='Single Mailer'
                    icon='envelope'
                    onClick={() => setViewMailSend(true)}
                  />
                )}
              </div>
            </div>
          </div>

          {publicFieldsets.length > 0 && (
            <div className='form'>
              {user.canWrite && (
                <div className='controls'>
                  <Icon
                    className='edit'
                    title='Edit Fields'
                    onClick={() => setIsEditing(!isEditing)}
                    icon={isEditing ? 'save' : 'pencil'}
                  />
                </div>
              )}
              <br />
              {publicFieldsets.map((fs) => {
                return (
                  <FieldsetDisplay
                    key={`${submission.id}-fs-${fs.id}`}
                    hasValidResponses={fieldsetHasValidResponses(
                      fields.filter((f) => f.fieldset_id == fs.id),
                      responses
                    )}
                    fieldset={fs}
                    isEditing={isEditing}
                  >
                    {partitionFields(
                      sortable(fields.filter((f) => f.fieldset_id === fs.id))
                    ).map((row, i) => {
                      return (
                        <div
                          className='input-row'
                          key={`${submission.id}-fs-${fs.id}-${i}`}
                        >
                          {row.map((field) => {
                            return (
                              <FieldResponseDisplay
                                key={`${submission.id}-f-${field.id}`}
                                field={field}
                                response={relatedResponse(field, responses)}
                                setResponses={setResponses}
                                setQuotaOpt={setQuotaOpt}
                                setPrevQuotaOpt={setPrevQuotaOpt}
                                setShowQuotaStatusModal={
                                  setShowQuotaStatusModal
                                }
                                isEditing={isEditing}
                                group={groupState}
                                adminField={false}
                                revalidate={reloadQuotaFieldData}
                              />
                            );
                          })}
                        </div>
                      );
                    })}
                  </FieldsetDisplay>
                );
              })}
            </div>
          )}

          {privateFieldsets.length > 0 && (
            <div className='form'>
              {privateFieldsets.map((fs) => {
                return (
                  <FieldsetDisplay
                    key={`${submission.id}-fs-${fs.id}`}
                    hasValidResponses={fieldsetHasValidResponses(
                      fields.filter((f) => f.fieldset_id == fs.id),
                      responses
                    )}
                    fieldset={fs}
                    isEditing={isEditing}
                  >
                    {partitionFields(
                      sortable(fields.filter((f) => f.fieldset_id === fs.id))
                    ).map((row, i) => {
                      return (
                        <div
                          className='input-row'
                          key={`${submission.id}-fs-${fs.id}-${i}`}
                        >
                          {row.map((field) => {
                            return (
                              <FieldResponseDisplay
                                key={`${submission.id}-f-${field.id}`}
                                field={field}
                                response={relatedResponse(field, responses)}
                                setResponses={setResponses}
                                setQuotaOpt={setQuotaOpt}
                                setPrevQuotaOpt={setPrevQuotaOpt}
                                setShowQuotaStatusModal={
                                  setShowQuotaStatusModal
                                }
                                isEditing={isEditing}
                                group={groupState}
                                adminField={true}
                                revalidate={reloadQuotaFieldData}
                              />
                            );
                          })}
                        </div>
                      );
                    })}
                  </FieldsetDisplay>
                );
              })}
            </div>
          )}

          <div className='center controls no-parent bold'>
            <a title='Back' onClick={handleBack}>
              Save & Close
            </a>
          </div>

          <QuotaNotifyStatus
            showQuotaStatusModal={showQuotaStatusModal}
            toggleQuotaStatusModal={closeQuotaModal}
            quotaOpt={quotaOpt}
            prevQuotaOpt={prevQuotaOpt}
          />
          <SingleMailerModal
            mailers={selectableMailers}
            viewMailSend={viewMailSend}
            closeModal={() => setViewMailSend(false)}
            fields={fields}
            responses={responses}
            submissionId={submission.id}
            sendingFromGlobal={false}
            updateDeliveries={updateDeliveries}
          />
          <SubmissionDeliveries
            handleMailerButton={() => setViewMailSend(true)}
            deliveries={deliveries}
            setDeliveries={setDeliveries}
            subID={submission.id}
            user={user}
          />
        </div>
      )}
      {(groupState?.id || canBecomeParent) && (
        <LinkedSubmissionsModal
          showLinkedModal={showLinkedModal}
          toggleLinkedModal={() => setShowLinkedModal(false)}
          isParentSubmission={isParent}
          canBecomeParent={canBecomeParent}
          setIsLoading={setIsLoading}
          setGroupState={setGroupState}
        />
      )}
    </SubmissionContext.Provider>
  );
};

export default SubmissionShow;
